import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  type FieldFunctionOptions,
  type FieldMergeFunction,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { ClerkApp, useAuth, useOrganization, useUser } from "@clerk/remix";
import { getAuth, rootAuthLoader } from "@clerk/remix/ssr.server";
import { datadogRum } from "@datadog/browser-rum";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useNavigation,
  useRouteError,
} from "@remix-run/react";
import { featureFlagSvc } from "@repo/services/featureFlag";
import { identitySvc } from "@repo/services/identity";
import {
  cn,
  FeatureFlagsContext,
  LogoBlueOnWhite,
  PrimaryFullScreenSpinner,
  PrimaryUIProvider,
  TooltipProvider,
  UnhandledAppError,
} from "@repo/ui";
import { createLogger } from "@repo/utils";
import riveWasmUrl from "@rive-app/canvas/rive.wasm";
import tracer from "dd-trace";
import * as React from "react";
import { Suspense, useEffect } from "react";
import { ClientOnly } from "remix-utils/client-only";
import { match, P } from "ts-pattern";
import logoBlueOnWhite from "~/assets/brand/logoBlueOnWhite.svg";
import interFont from "~/fonts/inter/inter.css?url";
import possibleTypes from "~/gql/client/possibleTypes";
import { customScalarsLink } from "~/gql/customScalars";
import globalsStylesheetUrl from "~/styles/globals.css?url";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: globalsStylesheetUrl },
  { rel: "stylesheet", href: interFont },
];

const logger = createLogger({
  name: "app.root",
});

export const loader = async (args: LoaderFunctionArgs) => {
  return await tracer.trace("app.root", async () => {
    return rootAuthLoader(args, async (_postClerkArgs) => {
      let featureFlags = new Map<string, boolean>();
      const auth = await getAuth(args);
      const identity = await identitySvc.createIdentityFromClerkAuth(auth);

      if (identity) {
        identitySvc.traceIdentity(identity);
        featureFlags = await featureFlagSvc.evaluateFeatureFlags({
          context: { identity },
        });
      }
      return { featureFlags: Object.fromEntries(featureFlags) };
    });
  });
};

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <title>Primary</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link
          rel="preload"
          href={riveWasmUrl}
          as="fetch"
          crossOrigin="anonymous"
        />
        <Meta />
        <Links />
      </head>
      <body className="h-dvh overflow-hidden">
        <TooltipProvider>{children}</TooltipProvider>

        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function GlobalLoading() {
  const navigation = useNavigation();
  const active = navigation.state !== "idle";

  const ref = React.useRef<HTMLDivElement>(null);
  const [animating, setAnimating] = React.useState(false);

  React.useEffect(() => {
    if (!ref.current) return;
    if (active) setAnimating(true);

    Promise.allSettled(
      ref.current.getAnimations().map(({ finished }) => finished),
    )
      .then(() => !active && setAnimating(false))
      .catch(logger.error);
  }, [active]);

  return (
    <div
      role="progressbar"
      aria-hidden={!active}
      aria-valuetext={active ? "Loading" : undefined}
      className="fixed inset-x-0 top-0 z-global-loading h-0.5 animate-pulse"
    >
      <div
        ref={ref}
        className={cn(
          "h-full bg-gradient-to-r from-brand-200 to-brand-500 transition-all duration-500 ease-in-out",
          match([navigation.state, animating])
            .with(["idle", false], () => "w-0 opacity-0 transition-none")
            .with(["submitting", P._], () => "w-5/12")
            .with(["loading", P._], () => "w-10/12")
            .otherwise(() => "w-full"),
        )}
      />
    </div>
  );
}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>();

  const user = useUser();
  const { organization } = useOrganization();

  useEffect(() => {
    if (user.user) {
      datadogRum.setUser({
        id: user.user.id,
      });
    }
  }, [user.user, user.user?.id]);

  useEffect(() => {
    if (organization?.id && organization.id !== "") {
      datadogRum.setUserProperty("organisation_id", organization.id);
    }
    if (organization?.name && organization.name !== "") {
      datadogRum.setUserProperty("organisation_name", organization.name);
    }
  }, [organization, organization?.id, organization?.name]);

  return (
    <ClientOnly>
      {() => (
        <Suspense fallback={<PrimaryFullScreenSpinner />}>
          <FeatureFlagsContext.Provider value={data.featureFlags}>
            <PrimaryUIProvider>
              <ApolloWrapper>
                <Outlet />
              </ApolloWrapper>
            </PrimaryUIProvider>
            <GlobalLoading />
          </FeatureFlagsContext.Provider>
        </Suspense>
      )}
    </ClientOnly>
  );
}

export function RootErrorBoundary() {
  const error = useRouteError();

  logger.error({ error });

  return (
    <UnhandledAppError
      Container={({ children }) => <div className="p-8">{children}</div>}
      logo={<LogoBlueOnWhite src={logoBlueOnWhite} />}
    />
  );
}

export default ClerkApp(AppWithProviders);

export const ErrorBoundary = RootErrorBoundary;

const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    possibleTypes: possibleTypes.possibleTypes,
    typePolicies: {
      CurrencyAmount: {
        merge: true,
      },
      Query: {
        fields: {
          externalBankAccountDataInstitutions: {
            keyArgs: ["filter", "distinct"],
            merge: paginatedMergeFunction("institutions"),
          },
          deposits: {
            keyArgs: ["order"],
            merge: paginatedMergeFunction("deposits"),
          },
          payouts: {
            keyArgs: ["filter", "order"],
            merge: paginatedMergeFunction("payouts"),
          },
          transactions: {
            keyArgs: ["filter", "order"],
            merge: paginatedMergeFunction("transactions"),
          },
          transfers: {
            keyArgs: ["filter", "order"],
            merge: paginatedMergeFunction("transfers"),
          },
          beneficiaryAccounts: {
            keyArgs: ["filter"],
            merge: paginatedMergeFunction("beneficiaryAccounts"),
          },
          events: {
            keyArgs: ["filter"],
            merge: paginatedMergeFunction("events"),
          },
          macquarieTransactionImportFiles: {
            keyArgs: ["order"],
            merge: paginatedMergeFunction("macquarieTransactionImportFiles"),
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      errorPolicy: "all",
    },
  },
});

function ApolloWrapper({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  let graphqlEndpoint = "/graphql";
  let crossOrigin = false;

  if (window.location.host === "staging-ops.primary.tech") {
    graphqlEndpoint = "https://staging-papi2.primary.tech/graphql";
    crossOrigin = true;
  } else if (window.location.host === "localhost:3000") {
    graphqlEndpoint = "http://localhost:3005/graphql";
    crossOrigin = true;
  }

  const authLink = setContext(async (_, { headers }) => {
    if (!crossOrigin) {
      return { headers };
    }

    const token = await auth.getToken();
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : "",
      },
    };
  });

  apolloClient.setLink(
    ApolloLink.from([
      authLink,
      customScalarsLink,
      (operation, forward) => {
        operation.setContext(
          ({ headers }: { headers: Record<string, string> }) => ({
            headers: {
              "X-Page-Origin": window.location.pathname,
              ...headers,
            },
          }),
        );
        return forward(operation);
      },
      new HttpLink({
        uri: graphqlEndpoint,
        credentials: "same-origin",
      }),
    ]),
  );

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}

function paginatedMergeFunction(
  itemsKey: string,
): FieldMergeFunction<
  any,
  any,
  FieldFunctionOptions<Record<string, any>, Record<string, any>>
> {
  return function merge(existing, incoming) {
    if (!existing || !incoming) {
      return incoming;
    }

    const existingRefs = new Set<string>(
      existing[itemsKey].map((i: { __ref: string }) => i.__ref),
    );
    const newItems = incoming[itemsKey].filter(
      (i: { __ref: string }) => !existingRefs.has(i.__ref),
    );

    return {
      ...existing,
      ...incoming,
      [itemsKey]: [...existing[itemsKey], ...newItems],
    };
  };
}
